import { PowerSyncContext, useQuery } from '@powersync/react';
import { Navigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { compileAsapQuery, createBlock, sortKeyAfter, trashBlock } from '@flow/domain';
import {
  applyLocalOperations,
  ensureSearchIndex,
  flushSearchIndex,
  getLocalJournalMode,
  getLocalSynchronous,
  queries,
  readEffectiveTags,
  rebuildAll,
  rebuildSearchIndex,
  rebuildSubtree,
  referenceEffectiveTags,
  search,
  SEARCH_STATEMENTS,
  searchIndexSize,
  setLocalSynchronous,
} from '@flow/sync';

import { APP_VERSION } from '../config.js';
import { useDevHandles } from '../db/dev-handles.js';
import type { LocalDatabase } from '../db/powersync.js';
import { t } from '../i18n/es.js';
import { useSession } from '../session.jsx';

/**
 * `/perf` (f0-performance-baseline, docs/12 §4): warm-up + N runs of the ASAP query with and
 * without area lens over the synced local database, cost of `effective_tags` rebuilds and local
 * capture latency. Results as JSON (also on `window.__flowPerf` for Playwright).
 */

const WARMUP = 5;
const RUNS = 50;

interface QueryStat {
  label: string;
  runs: number;
  rows: number;
  p50_ms: number;
  p95_ms: number;
  max_ms: number;
}

export interface PerfResult {
  kind: 'asap-web';
  app_version: string;
  user_agent: string;
  vfs: string;
  sdk: string;
  date: string;
  blocks: number;
  /** From opening the local database to the first completed checkpoint (null if not synced). */
  first_sync_ms: number | null;
  effective_tags: {
    rows: number;
    rebuild_all_ms: number;
    rebuild_all_again_ms: number;
    rebuild_subtree_ms: number;
    matches_reference: boolean;
    reference_query_ms: number;
  };
  queries: QueryStat[];
  /** Capture latency with the sync stream connected (normal use), no reactive views mounted. */
  capture: { runs: number; p50_ms: number; p95_ms: number; budget_ms: number };
  /**
   * Same with the reactive queries of the block screen mounted, which is how the app runs
   * (f1-local-write-latency, RNF-01): the save, the read that follows it and the time until the
   * list shows the change.
   */
  capture_with_views: {
    runs: number;
    p50_ms: number;
    p95_ms: number;
    budget_ms: number;
    within_budget: boolean;
  };
  capture_next_read: { p50_ms: number; p95_ms: number };
  /** Time until the mounted list shows the change (docs/12 §4: p95 < 100 ms). */
  capture_view_update: {
    p50_ms: number;
    p95_ms: number;
    timeouts: number;
    budget_ms: number;
    within_budget: boolean;
  };
  /** Same with the sync stream disconnected (isolates lock contention with the sync connection). */
  capture_disconnected: { runs: number; p50_ms: number; p95_ms: number };
  /** Same with `PRAGMA synchronous = NORMAL` and `OFF` (durability trade-off, see report). */
  capture_synchronous_normal: { runs: number; p50_ms: number; p95_ms: number };
  capture_synchronous_off: { runs: number; p50_ms: number; p95_ms: number };
  journal_mode: string;
  synchronous: string;
  /** Local full-text search (f0-local-search, docs/12 §4: p95 < 150 ms). */
  search: {
    indexed: number;
    rebuild_ms: number;
    queries: { term: string; runs: number; hits: number; p50_ms: number; p95_ms: number }[];
    budget_ms: number;
    within_budget: boolean;
  };
  budget_ms: number;
  within_budget: boolean;
}

declare global {
  interface Window {
    __flowPerf?: PerfResult;
    /** The open local database, for the write-latency probe (apps/web/perf/probe-writes.ts). */
    __flowDb?: LocalDatabase['db'];
    /** Search module, for the full-text index probe (apps/web/perf/probe-search.browser.js). */
    __flowSearch?: {
      statements: readonly string[];
      ensureSearchIndex: typeof ensureSearchIndex;
      flushSearchIndex: typeof flushSearchIndex;
      rebuildSearchIndex: typeof rebuildSearchIndex;
      search: typeof search;
    };
  }
}

function percentile(values: number[], p: number): number {
  const sorted = [...values].sort((a, b) => a - b);
  const index = Math.min(sorted.length - 1, Math.max(0, Math.ceil((p / 100) * sorted.length) - 1));
  return Math.round((sorted[index] ?? 0) * 100) / 100;
}

/**
 * Milliseconds until the mounted block list shows the block (null after 5 s). The list is the same
 * reactive query the app screen uses, so this is the time the user waits to see the change.
 */
function waitForBlockInList(id: string): Promise<number | null> {
  const started = performance.now();
  const shown = () => document.querySelector(`[data-testid="perf-views"] [data-block-id="${id}"]`);
  return new Promise((resolve) => {
    if (shown()) {
      resolve(0);
      return;
    }
    const observer = new MutationObserver(() => {
      if (!shown()) return;
      observer.disconnect();
      clearTimeout(timer);
      resolve(Math.round((performance.now() - started) * 100) / 100);
    });
    const timer = setTimeout(() => {
      observer.disconnect();
      resolve(null);
    }, 5000);
    observer.observe(document.body, { subtree: true, childList: true, characterData: true });
  });
}

async function timed<T>(fn: () => Promise<T>): Promise<{ result: T; ms: number }> {
  const started = performance.now();
  const result = await fn();
  return { result, ms: Math.round((performance.now() - started) * 100) / 100 };
}

async function measure(
  local: LocalDatabase,
  label: string,
  sql: string,
  params: (string | number | null)[],
): Promise<QueryStat> {
  for (let i = 0; i < WARMUP; i += 1) await local.db.getAll(sql, params);
  const samples: number[] = [];
  let rows = 0;
  for (let i = 0; i < RUNS; i += 1) {
    const { result, ms } = await timed(() => local.db.getAll(sql, params));
    rows = result.length;
    samples.push(ms);
  }
  return {
    label,
    runs: RUNS,
    rows,
    p50_ms: percentile(samples, 50),
    p95_ms: percentile(samples, 95),
    max_ms: Math.max(...samples),
  };
}

async function runBenchmark(
  local: LocalDatabase,
  userId: string,
  mountViews: (mounted: boolean) => Promise<void>,
): Promise<PerfResult> {
  const db = local.db;
  const one = async <T,>(q: { sql: string; params: (string | number | null)[] }) =>
    (await db.getAll<T>(q.sql, q.params))[0];
  const workspace = await one<{ id: string }>(queries.personalWorkspace());
  if (!workspace) throw new Error(t.perf.needsData);
  const blocks = (await one<{ n: number }>(queries.countBlocks(workspace.id)))?.n ?? 0;
  if (blocks === 0) throw new Error(t.perf.needsData);
  const area = await one<{ id: string }>(queries.taxonomyByKey(workspace.id, 'area'));
  const lens = area
    ? await one<{ id: string; name: string }>(queries.tagByName(area.id, 'Trabajo'))
    : undefined;
  const stage = await one<{ id: string }>(queries.captureStage(workspace.id));
  if (!area || !lens) throw new Error('Taxonomía «area» o etiqueta «Trabajo» no encontradas.');

  const scope = { workspaceId: workspace.id, taxonomyId: area.id };
  const full = await timed(() => rebuildAll(db, scope));
  const again = await timed(() => rebuildAll(db, scope));
  const project = await one<{ id: string }>(queries.projectWithChildren(workspace.id));
  const subtree = project ? await timed(() => rebuildSubtree(db, scope, project.id)) : { ms: 0 };
  const reference = await timed(() => referenceEffectiveTags(db, scope));
  const stored = await readEffectiveTags(db, area.id);
  const key = (r: { block_id: string; tag_id: string | null; inherited_from: string | null }) =>
    `${r.block_id}|${r.tag_id ?? ''}|${r.inherited_from ?? ''}`;
  const a = new Set(reference.result.map(key));
  const b = new Set(stored.map(key));
  const matches = a.size === b.size && [...a].every((k) => b.has(k));

  const today = new Date().toISOString().slice(0, 10);
  const periodStart = new Date(Date.now() - 7 * 86_400_000).toISOString();
  const base = { workspace_id: workspace.id, period_start: periodStart, today, user_id: userId };
  const withLens = compileAsapQuery({ ...base, lens: { taxonomy_id: area.id, tag_id: lens.id } });
  const withoutLens = compileAsapQuery(base);
  const results = [
    await measure(local, `asap · lente ${lens.name}`, withLens.sql, withLens.params),
    await measure(local, 'asap · sin lente', withoutLens.sql, withoutLens.params),
  ];

  // Local capture latency (RNF-01): create through the domain command, then trash the block.
  // `views` also times the read that follows the save and how long the mounted list takes to show
  // the new block (f1-local-write-latency).
  const captureRound = async (label: string, views = false) => {
    const samples: number[] = [];
    const reads: number[] = [];
    const updates: number[] = [];
    let timeouts = 0;
    // With the list mounted the new block must land at its end, so it is visible in the list.
    let lastKey: string | null = views
      ? ((await one<{ last: string | null }>(queries.lastSortKey(workspace.id)))?.last ?? null)
      : null;
    for (let i = 0; i < RUNS; i += 1) {
      lastKey = views ? sortKeyAfter(lastKey) : null;
      const ops = createBlock({
        workspace_id: workspace.id,
        created_by: userId,
        title: `Captura perf ${label} ${i + 1}`,
        stage_id: stage?.id ?? null,
        sort_key: lastKey ?? sortKeyAfter(null),
      });
      const shown = views ? waitForBlockInList(ops[0]!.id) : null;
      const { ms } = await timed(() => applyLocalOperations(db, ops));
      samples.push(ms);
      if (views) {
        const byId = queries.blockById(ops[0]!.id);
        const read = await timed(() => db.getOptional(byId.sql, byId.params));
        reads.push(read.ms);
        const at = await shown!;
        if (at === null) timeouts += 1;
        else updates.push(at);
      }
      await applyLocalOperations(db, trashBlock({ id: ops[0]!.id }).operations);
    }
    return { samples, reads, updates, timeouts };
  };
  const journalMode = await getLocalJournalMode(db);
  const synchronous = await getLocalSynchronous(db);
  // With the reactive views of the block screen mounted: the realistic number (RNF-01).
  await mountViews(true);
  const withViews = await captureRound('con vistas', true);
  await mountViews(false);
  const captureSamples = (await captureRound('conectado')).samples;
  await db.disconnect();
  const captureDisconnected = (await captureRound('desconectado')).samples;
  await setLocalSynchronous(db, 'normal');
  const captureNormal = (await captureRound('normal')).samples;
  await setLocalSynchronous(db, 'off');
  const captureOff = (await captureRound('off')).samples;
  await setLocalSynchronous(db, synchronous);
  await db.connect(local.connector);

  // Full-text search: frequent term, rare term, two-letter prefix and two words (50 runs each).
  const searchTerms = ['revisar', 'lisboa', 'pr', 'renovar seguro'];
  const searchStats: PerfResult['search']['queries'] = [];
  for (const term of searchTerms) {
    for (let i = 0; i < WARMUP; i += 1) await search(db, term);
    const samples: number[] = [];
    let hits = 0;
    for (let i = 0; i < RUNS; i += 1) {
      const { result, ms } = await timed(() => search(db, term));
      hits = result.length;
      samples.push(ms);
    }
    searchStats.push({
      term,
      runs: RUNS,
      hits,
      p50_ms: percentile(samples, 50),
      p95_ms: percentile(samples, 95),
    });
  }
  const searchRebuild = await timed(() => rebuildSearchIndex(db));
  const indexed = await searchIndexSize(db);

  return {
    kind: 'asap-web',
    app_version: APP_VERSION,
    user_agent: navigator.userAgent,
    vfs: local.vfs.name,
    sdk: db.sdkVersion,
    date: new Date().toISOString(),
    blocks,
    first_sync_ms: db.currentStatus.lastSyncedAt
      ? db.currentStatus.lastSyncedAt.getTime() - local.openedAt
      : null,
    effective_tags: {
      rows: stored.length,
      rebuild_all_ms: full.ms,
      rebuild_all_again_ms: again.ms,
      rebuild_subtree_ms: subtree.ms,
      matches_reference: matches,
      reference_query_ms: reference.ms,
    },
    queries: results,
    capture: {
      runs: RUNS,
      p50_ms: percentile(captureSamples, 50),
      p95_ms: percentile(captureSamples, 95),
      budget_ms: 100,
    },
    capture_with_views: {
      runs: RUNS,
      p50_ms: percentile(withViews.samples, 50),
      p95_ms: percentile(withViews.samples, 95),
      budget_ms: 100,
      within_budget: percentile(withViews.samples, 95) < 100,
    },
    capture_next_read: {
      p50_ms: percentile(withViews.reads, 50),
      p95_ms: percentile(withViews.reads, 95),
    },
    capture_view_update: {
      p50_ms: percentile(withViews.updates, 50),
      p95_ms: percentile(withViews.updates, 95),
      timeouts: withViews.timeouts,
      budget_ms: 100,
      within_budget: withViews.timeouts === 0 && percentile(withViews.updates, 95) < 100,
    },
    capture_disconnected: {
      runs: RUNS,
      p50_ms: percentile(captureDisconnected, 50),
      p95_ms: percentile(captureDisconnected, 95),
    },
    capture_synchronous_normal: {
      runs: RUNS,
      p50_ms: percentile(captureNormal, 50),
      p95_ms: percentile(captureNormal, 95),
    },
    capture_synchronous_off: {
      runs: RUNS,
      p50_ms: percentile(captureOff, 50),
      p95_ms: percentile(captureOff, 95),
    },
    journal_mode: journalMode,
    synchronous,
    search: {
      indexed,
      rebuild_ms: searchRebuild.ms,
      queries: searchStats,
      budget_ms: 150,
      within_budget: searchStats.every((q) => q.p95_ms < 150),
    },
    budget_ms: 200,
    within_budget: results.every((q) => q.p95_ms < 200),
  };
}

export function PerfPage() {
  const { status, user, local } = useSession();
  if (status === 'loading') return <p className="muted">{t.app.loading}</p>;
  if (status === 'anonymous' || !user) return <Navigate to="/login" />;
  if (status === 'opening' || !local) return <p className="muted">{t.app.openingDatabase}</p>;
  return (
    <PowerSyncContext.Provider value={local.db}>
      <PerfRunner local={local} userId={user.id} />
    </PowerSyncContext.Provider>
  );
}

/**
 * The reactive queries the block screen mounts (`HomePage`), rendered as a list so the measurement
 * includes what the user waits for: re-run of the watched queries plus React rendering.
 */
function WatchedViews() {
  const workspaceQuery = queries.personalWorkspace();
  const { data: workspaces } = useQuery<{ id: string }>(workspaceQuery.sql, workspaceQuery.params);
  const workspaceId = workspaces[0]?.id ?? '';
  const blocksQuery = queries.actionableBlocks(workspaceId);
  const stageQuery = queries.captureStage(workspaceId);
  const lastKeyQuery = queries.lastSortKey(workspaceId);
  const pendingQuery = queries.pendingUploads();
  const { data: blocks } = useQuery<{ id: string; title: string }>(
    blocksQuery.sql,
    blocksQuery.params,
  );
  useQuery<{ id: string }>(stageQuery.sql, stageQuery.params);
  useQuery<{ last: string | null }>(lastKeyQuery.sql, lastKeyQuery.params);
  useQuery<{ n: number }>(pendingQuery.sql, pendingQuery.params);
  return (
    <ul className="blocks" data-testid="perf-views" aria-label={t.perf.views}>
      {blocks.map((block) => (
        <li key={block.id} data-block-id={block.id}>
          {block.title}
        </li>
      ))}
    </ul>
  );
}

function PerfRunner({ local, userId }: { local: LocalDatabase; userId: string }) {
  const [result, setResult] = useState<PerfResult | null>(null);
  const [views, setViews] = useState(false);
  useDevHandles(local, userId);
  useEffect(() => {
    window.__flowDb = local.db;
    window.__flowSearch = {
      statements: SEARCH_STATEMENTS,
      ensureSearchIndex,
      flushSearchIndex,
      rebuildSearchIndex,
      search,
    };
    return () => {
      delete window.__flowDb;
      delete window.__flowSearch;
    };
  }, [local.db]);
  // `/perf?auto=1` runs the benchmark on load (manual runs on the iOS simulator).
  const auto = new URLSearchParams(window.location.search).get('auto') === '1';
  useEffect(() => {
    if (!auto) return;
    const timer = setTimeout(() => void run(), 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on load
  }, [auto]);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [copied, setCopied] = useState(false);

  /** Mounts or unmounts the reactive views and waits for their first results. */
  const mountViews = async (mounted: boolean) => {
    setViews(mounted);
    await new Promise((resolve) => setTimeout(resolve, mounted ? 2000 : 500));
  };

  async function run() {
    setRunning(true);
    setError(null);
    setCopied(false);
    try {
      const value = await runBenchmark(local, userId, mountViews);
      window.__flowPerf = value;
      setResult(value);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  }

  const json = result ? JSON.stringify(result, null, 2) : '';
  return (
    <main className="page">
      <h1>{t.perf.title}</h1>
      <p className="muted">{t.perf.intro}</p>
      <div className="row">
        <button type="button" disabled={running} onClick={() => void run()}>
          {running ? t.perf.running : t.perf.run}
        </button>
        {result ? (
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(json).then(() => setCopied(true));
            }}
          >
            {copied ? t.perf.copied : t.perf.copy}
          </button>
        ) : null}
      </div>
      {error ? (
        <p role="alert" className="error">
          {error}
        </p>
      ) : null}
      {views ? <WatchedViews /> : null}
      {result ? (
        <>
          <p data-testid="perf-summary" data-within-budget={String(result.within_budget)}>
            ASAP p95: {result.queries.map((q) => `${q.label} ${q.p95_ms} ms`).join(' · ')} · captura
            p95 {result.capture.p95_ms} ms · effective_tags {result.effective_tags.rebuild_all_ms}{' '}
            ms · búsqueda p95:{' '}
            {result.search.queries.map((q) => `${q.term} ${q.p95_ms} ms`).join(' · ')} · índice{' '}
            {result.search.indexed} filas, reconstrucción {result.search.rebuild_ms} ms · captura
            con vistas p95 {result.capture_with_views.p95_ms} ms (lectura siguiente{' '}
            {result.capture_next_read.p95_ms} ms · vista {result.capture_view_update.p95_ms} ms
            {result.capture_view_update.within_budget ? '' : ' ⚠ fuera de presupuesto'})
          </p>
          <textarea
            data-testid="perf-json"
            readOnly
            rows={24}
            value={json}
            style={{ width: '100%' }}
          />
        </>
      ) : null}
    </main>
  );
}
